'use client';

import { useQuery } from '@tanstack/react-query';
import type { Address } from 'viem';

interface WalletFundingResponse {
  holder: string;
  /** Base-unit strings, or null when the balance couldn't be read. */
  usdc: string | null;
  takeover: string | null;
}

function safeBig(s: string | null | undefined): bigint | null {
  if (s == null) return null;
  try {
    return BigInt(s);
  } catch {
    return null;
  }
}

/**
 * The holder's USDC + TAKEOVER wallet balances — the two tokens Land
 * Protection pulls top-ups from. Used by the protection panel to warn when
 * the wallet is too low to fund its own top-ups (which would otherwise skip
 * onchain as `RedemptionSkipped`).
 *
 * `null` for a token means "unknown" (read failed / token not configured);
 * callers must treat that as "don't warn" rather than "empty wallet".
 */
export function useProtectionWalletFunding(address: Address | null) {
  const { data, isLoading } = useQuery<WalletFundingResponse>({
    queryKey: ['protection-wallet-funding', address?.toLowerCase() ?? null],
    enabled: !!address,
    queryFn: async () => {
      const res = await fetch(`/api/protection/wallet-funding?address=${address}`);
      if (!res.ok) throw new Error(`wallet-funding: HTTP ${res.status}`);
      return (await res.json()) as WalletFundingResponse;
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  return {
    usdcRaw: safeBig(data?.usdc),
    takeoverRaw: safeBig(data?.takeover),
    isLoading,
  };
}
